import { SearchOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Form, Input, DatePicker, Select, Row, Col, Typography, Button, Spin, Divider, message, Radio, Switch, Tooltip, Space, Empty, InputNumber } from 'antd';
import dayjs from 'dayjs';
import React, { useState, useEffect } from 'react';


import { OrderForm } from '../../pages/orders/OrderFormPage';
import { shipmentApi, ShipmentMethodResponse } from '../../services/api';
import { customerService } from '../../services/customerService';
import { exchangeRateService } from '../../services/exchangeRateService';
import { TaxType } from '../../services/taxApi';

import type { RadioChangeEvent } from 'antd';

const { Option } = Select;
const { Text, Title } = Typography;

// Sipariş tipi enum
// Sipariş tipi enum
export enum OrderType {
  WHOLESALE_SALES = 'WS',
  WHOLESALE_PURCHASE = 'BP',
  EXPENSE_SALES = 'EXP',
  EXPENSE_PURCHASE = 'EP'
}

interface Customer {
  customerCode: string;
  customerName: string;
  address?: string;
}

interface Vendor {
  vendorCode: string;
  vendorName: string;
  address?: string;
}

interface Office {
  officeCode: string;
  officeName: string;
}

interface Warehouse {
  warehouseCode: string;
  warehouseName: string;
}

interface Currency {
  currencyCode: string;
  currencyName?: string;
  currencyDescription?: string;
  isBlocked?: boolean;
}

interface CustomerAddress {
  postalAddressId: string;
  addressName: string;
  address1: string;
  address2?: string;
  cityName?: string;
  countryName?: string;
  isDefault?: boolean;
  isDefaultShippingAddress?: boolean;
  isDefaultBillingAddress?: boolean;
}

// Sevkiyat yöntemi arayüzü - API'den gelen veri yapısına uygun
interface ShipmentMethod {
  ShipmentMethodCode: string;
  shipmentMethodName?: string;
  shipmentMethodDescription?: string;
  description?: string;
  transportModeCode?: string;
  transportModeDescription?: string;
  isBlocked: boolean;
  isDefault?: boolean;
}

export interface OrderHeaderProps {
  form: any;
  customers: Customer[];
  vendors: Vendor[];
  offices: Office[];
  warehouses: Warehouse[];
  currencies: Currency[];
  loadingCurrencies: boolean;
  isReturn: boolean;
  setIsReturn: (value: boolean) => void;
  isEOrder: boolean;
  setIsEOrder: (value: boolean) => void;
  onCurrencyChange?: (currencyCode: string) => void;
  onExchangeRateChange?: (rate: number) => void;
  onExchangeRateSourceChange?: (e: RadioChangeEvent) => void;
  onTaxTypeChange?: (taxTypeMode: string) => void;
  orderType: OrderType;
  taxTypes?: TaxType[];
  loadingTaxTypes?: boolean;
  onFormChange: (field: keyof OrderForm, value: any) => void;
  onCustomerChange: (customerCode: string) => void;
  isEdit: boolean;
}

const OrderHeader: React.FC<OrderHeaderProps> = ({
  form,
  customers,
  vendors,
  offices,
  warehouses,
  currencies,
  loadingCurrencies,
  isReturn,
  setIsReturn,
  isEOrder,
  setIsEOrder,
  onCurrencyChange,
  onExchangeRateChange,
  onExchangeRateSourceChange,
  onTaxTypeChange,
  orderType,
  taxTypes = [],
  loadingTaxTypes = false
}) => {
  // State tanımlamaları
  const [customerAddresses, setCustomerAddresses] = useState<CustomerAddress[]>([]);
  const [vendorAddresses, setVendorAddresses] = useState<CustomerAddress[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState<boolean>(false);
  const [shipmentMethods, setShipmentMethods] = useState<ShipmentMethod[]>([]);
  const [loadingShipmentMethods, setLoadingShipmentMethods] = useState<boolean>(false);
  const [isCustomerCurrency, setIsCustomerCurrency] = useState<boolean>(false);
  const [isVendorCurrency, setIsVendorCurrency] = useState<boolean>(false);
  const [exchangeRateDisabled, setExchangeRateDisabled] = useState<boolean>(true);
  const [exchangeRate, setExchangeRate] = useState<number>(0);
  const [exchangeRateSource, setExchangeRateSource] = useState<string>('TCMB'); // TCMB varsayılan olarak seçili
  const [currentCurrencyCode, setCurrentCurrencyCode] = useState<string>('TRY'); // Varsayılan para birimi TRY
  
  // Sevkiyat yöntemlerini getir
  const fetchShipmentMethods = async () => {
    setLoadingShipmentMethods(true);
    try {
      const response = await shipmentApi.getShipmentMethods();
      if (response && response.length > 0) {
        // API'den gelen verileri ShipmentMethod arayüzüne uygun şekilde dönüştür
        const formattedMethods = response.map((method: ShipmentMethodResponse) => ({
          ...method,
          ShipmentMethodCode: method.shipmentMethodCode
        }));
        setShipmentMethods(formattedMethods);
        
        // Varsayılan olarak hiçbir sevkiyat yöntemi seçilmeyecek
        form.setFieldsValue({ ShipmentMethodCode: '' });
      }
    } catch (error) {
      console.error('Sevkiyat yöntemleri alınırken hata oluştu:', error);
      message.error('Sevkiyat yöntemleri alınırken hata oluştu');
    } finally {
      setLoadingShipmentMethods(false);
    }
  };

  // Bileşen yüklendiğinde sevkiyat yöntemlerini getir
  useEffect(() => {
    fetchShipmentMethods();
  }, []);
  
  // Vergi tiplerini izle ve "Vergisiz" seçeneğini varsayılan olarak ayarla
  useEffect(() => {
    if (taxTypes?.length > 0) {
      // "Vergisiz" vergi tipini bul
      const taxFreeType = taxTypes.find(tax => 
        tax.taxTypeDescription?.toLowerCase() === 'Vergisiz' || 
        tax.taxTypeCode === '4' // Vergisiz için kesin kod kontrolü
      );
      
      // Eğer "Vergisiz" vergi tipi varsa, onu varsayılan olarak ayarla
      if (taxFreeType) {
        form.setFieldsValue({ taxTypeCode: taxFreeType.taxTypeCode });
        // Varsayılan olarak "Vergisiz" seçildiğinde KDV oranlarını sıfırla
        handleTaxTypeChange(taxFreeType.taxTypeCode);
      }
    }
  }, [taxTypes]);
  
  // Vergi tipi değiştiğinde çalışacak fonksiyon
  const handleTaxTypeChange = (taxTypeCode: string) => {
    // Seçilen vergi tipini bul
    const selectedTaxType = taxTypes?.find(tax => tax.taxTypeCode === taxTypeCode);
    
    // Form alanını güncelle - bu önemli, API'ye gönderilecek
    form.setFieldsValue({ taxTypeCode: taxTypeCode });
    
    // Eğer "Vergisiz" seçildiyse tüm sipariş satırlarındaki KDV oranını 0 yap
    if (selectedTaxType && 
        (selectedTaxType.taxTypeCode === '4' || 
         selectedTaxType.taxTypeDescription === 'Vergisiz')) {
      
      // OrderForm bileşeninden gelen onTaxTypeChange fonksiyonunu çağır
      if (onTaxTypeChange) {
        onTaxTypeChange('vergisiz');
      }
    } else {
      // Başka bir vergi tipi seçildiyse, normal KDV oranlarını kullan (varsayılan %10)
      if (onTaxTypeChange) {
        onTaxTypeChange('normal'); // normal mod - varsayılan KDV oranı %10
      }
    }
  };

  // Müşteri adreslerini getir
  const fetchCustomerAddresses = async (customerId: string) => {
    if (!customerId) {
      return;
    }
    
    setLoadingAddresses(true);
    try {
      const response = await customerService.getCustomerAddresses(customerId);
      
      let addresses: CustomerAddress[] = [];
      
      if (response && response.data && response.data.length > 0) {
        addresses = response.data.map((addr: any) => ({
          postalAddressId: addr.postalAddressId,
          addressName: addr.addressName || 'Adres',
          address1: addr.address1 || '',
          address2: addr.address2 || '',
          cityName: addr.cityName || '',
          countryName: addr.countryName || '',
          isDefault: addr.isDefault || false,
          isDefaultShippingAddress: addr.isDefaultShippingAddress || false,
          isDefaultBillingAddress: addr.isDefaultBillingAddress || false
        }));
      }
      
      setCustomerAddresses(addresses);
      
      // Varsayılan adresi seç
      const defaultAddress = addresses.find(addr => addr.isDefault || addr.isDefaultShippingAddress);
      if (defaultAddress) {
        form.setFieldsValue({ customerAddressId: defaultAddress.postalAddressId });
      }
      
    } catch (error) {
      console.error('Müşteri adresleri alınırken hata oluştu:', error);
      message.error('Müşteri adresleri alınırken hata oluştu');
    } finally {
      setLoadingAddresses(false);
    }
  };

  // Tedarikçi adreslerini getir
  const fetchVendorAddresses = async (vendorId: string) => {
    if (!vendorId) {
      return;
    }
    
    setLoadingAddresses(true);
    try {
      const response = await customerService.getCustomerAddresses(vendorId);
      
      let addresses: CustomerAddress[] = [];
      
      if (response && response.data && response.data.length > 0) {
        addresses = response.data.map((addr: any) => ({
          postalAddressId: addr.postalAddressId,
          addressName: addr.addressName || 'Adres',
          address1: addr.address1 || '',
          address2: addr.address2 || '',
          cityName: addr.cityName || '',
          countryName: addr.countryName || '',
          isDefault: addr.isDefault || false,
          isDefaultShippingAddress: addr.isDefaultShippingAddress || false,
          isDefaultBillingAddress: addr.isDefaultBillingAddress || false
        }));
      }
      
      setVendorAddresses(addresses);
      
      // Varsayılan adresi seç
      const defaultAddress = addresses.find(addr => addr.isDefault || addr.isDefaultShippingAddress);
      if (defaultAddress) {
        form.setFieldsValue({ vendorAddressId: defaultAddress.postalAddressId });
      }
      
    } catch (error) {
      console.error('Tedarikçi adresleri alınırken hata oluştu:', error);
      message.error('Tedarikçi adresleri alınırken hata oluştu');
    } finally {
      setLoadingAddresses(false);
    }
  };

  // Döviz kuru getir
  const getExchangeRate = async (currencyCode: string, date: string, source: string) => {
    try {
      const response = await exchangeRateService.getExchangeRate(currencyCode, date, source);
      return response.exchangeRate || 1;
    } catch (error) {
      console.error('Döviz kuru alınırken hata oluştu:', error);
      return 1;
    }
  };

  // Para birimi değiştiğinde döviz kurunu getir
  const handleCurrencyChange = async (currencyCode: string) => {
    setCurrentCurrencyCode(currencyCode); // Seçilen para birimini state'e kaydet
    
    if (onCurrencyChange) {
      onCurrencyChange(currencyCode);
    }

    if (currencyCode === 'TRY') {
      setExchangeRate(1);
      setExchangeRateDisabled(true);
      form.setFieldsValue({ exchangeRate: 1 });
      if (onExchangeRateChange) {
        onExchangeRateChange(1);
      }
    } else {
      setExchangeRateDisabled(false);
      const today = dayjs().format('YYYY-MM-DD');
      const rate = await getExchangeRate(currencyCode, today, exchangeRateSource);
      setExchangeRate(rate);
      form.setFieldsValue({ exchangeRate: rate });
      if (onExchangeRateChange) {
        onExchangeRateChange(rate);
      }
    }
  };

  // Döviz kuru kaynağı değiştiğinde
  const handleExchangeRateSourceChange = async (e: RadioChangeEvent) => {
    const source = e.target.value;
    setExchangeRateSource(source);
    
    if (onExchangeRateSourceChange) {
      onExchangeRateSourceChange(e);
    }

    const currencyCode = form.getFieldValue('currencyCode');
    if (currencyCode && currencyCode !== 'TRY') {
      const today = dayjs().format('YYYY-MM-DD');
      const rate = await getExchangeRate(currencyCode, today, source);
      setExchangeRate(rate);
      form.setFieldsValue({ exchangeRate: rate });
      if (onExchangeRateChange) {
        onExchangeRateChange(rate);
      }
    }
  };

  return (
    <div className="order-header-mobile-friendly">
      <style>
        {`
          .order-header-mobile-friendly .ant-row {
            margin-bottom: 16px;
          }
          
          .order-header-mobile-friendly .ant-col {
            margin-bottom: 8px;
          }
          
          @media (max-width: 768px) {
            .order-header-mobile-friendly .ant-col {
              margin-bottom: 12px;
            }
            
            .order-header-mobile-friendly .ant-form-item {
              margin-bottom: 16px;
            }
          }
        `}
      </style>

      <Row gutter={[24, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Title level={4}>Sipariş Bilgileri</Title>
        </Col>

        <Col xs={24} sm={12} md={12} lg={12}>
          <Form.Item
            label="Sipariş Numarası"
            name="orderNumber"
            rules={[{ required: true, message: 'Sipariş numarası gereklidir' }]}
          >
            <Input placeholder="Sipariş numarası giriniz" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={12} lg={12}>
          <Form.Item
            label="Sipariş Tarihi"
            name="orderDate"
            rules={[{ required: true, message: 'Sipariş tarihi gereklidir' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder="Sipariş tarihi seçiniz"
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={12} lg={12}>
          {/* Sipariş Tipi seçimi kaldırıldı */}
        </Col>

        <Col xs={24} sm={12} md={6} lg={6}>
          <Form.Item label="İade Siparişi">
            <Switch
              checked={isReturn}
              onChange={setIsReturn}
              checkedChildren="İade"
              unCheckedChildren="Normal"
              style={{ backgroundColor: '#f5f5f5' }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 12]}>
        <Col xs={12} sm={6} md={3} lg={3}>
          <Form.Item label="E-Sipariş">
            <Switch
              checked={isEOrder}
              onChange={setIsEOrder}
              checkedChildren="Evet"
              unCheckedChildren="Hayır"
            />
          </Form.Item>
        </Col>

        <Col xs={12} sm={6} md={3} lg={3}>
          <Form.Item
            label="Müşteri/Tedarikçi"
            name={orderType === OrderType.WHOLESALE_SALES || orderType === OrderType.EXPENSE_SALES ? 'customerCode' : 'vendorCode'}
            rules={[{ required: true, message: 'Müşteri/Tedarikçi seçimi gereklidir' }]}
          >
            <Select
              showSearch
              placeholder="Seçiniz"
              optionFilterProp="children"
              onChange={(value) => {
                if (orderType === OrderType.WHOLESALE_SALES || orderType === OrderType.EXPENSE_SALES) {
                  fetchCustomerAddresses(value);
                } else {
                  fetchVendorAddresses(value);
                }
              }}
            >
              {orderType === OrderType.WHOLESALE_SALES || orderType === OrderType.EXPENSE_SALES ? (
                customers.map((customer) => (
                  <Option key={customer.customerCode} value={customer.customerCode}>
                    {customer.customerCode} - {customer.customerName}
                  </Option>
                ))
              ) : (
                vendors.map((vendor) => (
                  <Option key={vendor.vendorCode} value={vendor.vendorCode}>
                    {vendor.vendorCode} - {vendor.vendorName}
                  </Option>
                ))
              )}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={12} sm={6} md={3} lg={3}>
          <Form.Item
            label="Müşteri Adresi"
            name={orderType === OrderType.WHOLESALE_SALES || orderType === OrderType.EXPENSE_SALES ? 'customerAddressId' : 'vendorAddressId'}
          >
            <Select
              placeholder="Adres seçiniz"
              loading={loadingAddresses}
              disabled={loadingAddresses}
            >
              {(orderType === OrderType.WHOLESALE_SALES || orderType === OrderType.EXPENSE_SALES ? customerAddresses : vendorAddresses).map((address) => (
                <Option key={address.postalAddressId} value={address.postalAddressId}>
                  {address.addressName} - {address.address1}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6} lg={6}>
          <Form.Item
            label="Ofis"
            name="officeCode"
            rules={[{ required: true, message: 'Ofis seçimi gereklidir' }]}
          >
            <Select placeholder="Ofis seçiniz">
              {offices.map((office) => (
                <Option key={office.officeCode} value={office.officeCode}>
                  {office.officeName}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6} lg={6}>
          <Form.Item
            label="Depo"
            name="warehouseCode"
            rules={[{ required: true, message: 'Depo seçimi gereklidir' }]}
          >
            <Select placeholder="Depo seçiniz">
              {warehouses.map((warehouse) => (
                <Option key={warehouse.warehouseCode} value={warehouse.warehouseCode}>
                  {warehouse.warehouseName}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 12]}>
        <Col xs={12} sm={6} md={6} lg={6}>
          <Form.Item
            label="Para Birimi"
            name="currencyCode"
            rules={[{ required: true, message: 'Para birimi seçimi gereklidir' }]}
          >
            <Select
              placeholder="Para birimi seçiniz"
              loading={loadingCurrencies}
              onChange={handleCurrencyChange}
            >
              {currencies.map((currency) => (
                <Option key={currency.currencyCode} value={currency.currencyCode}>
                  {currency.currencyCode} - {currency.currencyName}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        // Döviz kuru alanı için Form.Item bileşeni
        <Col xs={12} sm={6} md={6} lg={6}>
          <Form.Item
            label="Döviz Kuru"
            name="exchangeRate"
            rules={[{ required: currentCurrencyCode !== 'TRY', message: 'Döviz kuru gereklidir' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.0001}
              precision={4}
              disabled={exchangeRateDisabled}
              onChange={(value) => {
                setExchangeRate(value || 0);
                if (onExchangeRateChange) {
                  onExchangeRateChange(value || 0);
                }
              }}
              addonAfter={<Tooltip title="TL karşılığı"><InfoCircleOutlined /></Tooltip>}
            />
          </Form.Item>
        </Col>
        
        // Döviz kuru kaynağı için Form.Item bileşeni
        <Col xs={12} sm={6} md={6} lg={6}>
          <Form.Item
            label="Kur Kaynağı"
            name="exchangeRateSource"
          >
            <Radio.Group
              onChange={(e) => {
                setExchangeRateSource(e.target.value);
                if (onExchangeRateSourceChange) {
                  onExchangeRateSourceChange(e);
                }
              }}
              disabled={exchangeRateDisabled}
            >
              <Radio value="TCMB">TCMB</Radio>
              <Radio value="custom">Manuel</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={24}>
          <Divider />
        </Col>
      </Row>

      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Sevkiyat Yöntemi"
            name="ShipmentMethodCode"
          >
            <Select
              placeholder="Sevkiyat yöntemi seçiniz"
              loading={loadingShipmentMethods}
              allowClear
            >
              {shipmentMethods.map((method) => (
                <Option key={method.ShipmentMethodCode} value={method.ShipmentMethodCode}>
                  {method.shipmentMethodName || method.description}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item
            label="Vergi Tipi"
            name="taxTypeCode"
          >
            <Select
              placeholder="Vergi tipi seçiniz"
              loading={loadingTaxTypes}
              onChange={handleTaxTypeChange}
            >
              {taxTypes.map((taxType) => (
                <Option key={taxType.taxTypeCode} value={taxType.taxTypeCode}>
                  {taxType.taxTypeDescription}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item
            label="Açıklama"
            name="description"
          >
            <Input.TextArea rows={3} placeholder="Sipariş ile ilgili notlar..." />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Teslim Tarihi"
            name="deliveryDate"
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder="Teslim tarihi seçiniz"
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label="Referans Numarası"
            name="referenceNumber"
          >
            <Input placeholder="Referans numarası giriniz" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item label="Notlar" name="notes">
            <Input.TextArea rows={3} placeholder="Sipariş ile ilgili notlar..." />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

export default OrderHeader;